import type { ReactNode } from "react";
import { ScrollView } from "react-native";
import { useLocalSearchParams, useRouter, type Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { View } from "@src/components";
import {
  INCIDENT_DEFAULT_DAYS,
  incidentPeriodRange,
  parseIncidentFilters,
  useFleetIncidentSummary,
  useTenantIncidentSummary,
  type IncidentSummaryPeriod,
  type ParsedIncidentFilters,
} from "@visioni/a3s-monitoring/app";
import { FLEET_INCIDENTS_SUBPATH, patriotToolsHref, TENANT_INCIDENTS_SUBPATH } from "../routes";
import { useListTimeZone } from "../hooks/useListTimeZone";
import { useTenantListFilters } from "../hooks/useTenantListFilters";
import { ActivationFilterBar } from "../components/ActivationFilterBar";
import { IncidentTotals } from "../components/IncidentTotals";
import { PeriodField } from "../components/PeriodField";
import { QueryNotice } from "../components/QueryNotice";
import { TimeBoundFields } from "../components/TimeBoundFields";
import { TimeZoneField } from "../components/TimeZoneField";

const blank = (value: string | undefined) => (value === "" ? undefined : value);
// The fleet has no site, so Default is always the viewer's own zone.
const noSiteTimeZone = () => undefined;

type Summary = ReturnType<typeof useFleetIncidentSummary>;

/**
 * The incident list for one period of the totals, in their zone and scope. It
 * names its own range, so it lists what the row counted rather than the list's
 * default window.
 */
function periodIncidentsHref(
  tenantId: string,
  subpath: string,
  filters: {
    readonly from?: string;
    readonly to?: string;
    readonly tz: string;
    readonly site?: string;
    readonly camera?: string;
  },
  row: { readonly period: IncidentSummaryPeriod; readonly label: string },
): Href {
  const { from, to, tz, site, camera } = filters;
  return patriotToolsHref(tenantId, subpath, {
    ...incidentPeriodRange({ from, to, tz }, row, new Date()),
    tz,
    site,
    camera,
  });
}

/**
 * One organisation's genuine incident and activation totals, narrowed by site
 * and camera like its incident list (`?site=&camera=&tz=&from=&to=&period=`).
 *
 * Defaults to the viewer's own zone even with a site selected: the totals are
 * the viewer's days, weeks or months.
 */
export function TenantIncidentTotalsScreen() {
  const router = useRouter();
  const { period: rawPeriod } = useLocalSearchParams<{ period?: string }>();
  const period = blank(rawPeriod);
  const {
    tenantId,
    values: listValues,
    scope,
    timeZone,
    fallback,
    onChange,
  } = useTenantListFilters({ viewerTimeZone: true });
  // Totals have no order; leaving it out also drops one carried over from a list link.
  const values = { ...listValues, order: undefined };
  const parsed =
    timeZone === undefined ? undefined : parseIncidentFilters({ ...values, tz: timeZone, period }, new Date());
  const ok = parsed?.ok === true ? parsed : undefined;
  const totals = useTenantIncidentSummary({
    filters: ok?.filters ?? { since: "" },
    period: ok?.period ?? "day",
    timeZone: timeZone ?? "UTC",
    enabled: ok !== undefined,
  });

  return (
    <TotalsPage
      filters={
        timeZone !== undefined && (
          <ActivationFilterBar
            values={{ ...values, tz: timeZone }}
            onChange={onChange}
            sites={scope.sites}
            cameras={scope.cameras}
            isLoading={scope.isLoading}
            timeZone={timeZone}
            fallback={fallback}
            offsetsAt={ok !== undefined ? new Date(ok.filters.since) : new Date()}
            fromPlaceholder={`Last ${String(INCIDENT_DEFAULT_DAYS)} days`}
            siteTimeZoneDefault={false}
            showOrder={false}
            trailing={<PeriodField value={period} onChange={(next) => router.setParams({ period: next })} />}
          />
        )
      }
      parsed={parsed}
      totals={totals}
      period={ok?.period ?? "day"}
      incidentsHrefFor={(label) =>
        periodIncidentsHref(
          tenantId,
          TENANT_INCIDENTS_SUBPATH,
          { ...values, tz: timeZone ?? "UTC" },
          { period: ok?.period ?? "day", label },
        )
      }
    />
  );
}

/** The Vision Intelligence fleet's genuine incident and activation totals (`?tz=&from=&to=&period=`). */
export function FleetIncidentTotalsScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ tenantId: string; from?: string; to?: string; tz?: string; period?: string }>();
  const from = blank(params.from);
  const to = blank(params.to);
  const period = blank(params.period);
  const { timeZone, fallback } = useListTimeZone({
    tz: blank(params.tz),
    site: undefined,
    siteTimeZone: noSiteTimeZone,
    onResolve: (tz) => router.setParams({ tz }),
  });
  const parsed =
    timeZone === undefined ? undefined : parseIncidentFilters({ from, to, tz: timeZone, period }, new Date());
  const ok = parsed?.ok === true ? parsed : undefined;
  const totals = useFleetIncidentSummary({
    filters: ok?.filters ?? { since: "" },
    period: ok?.period ?? "day",
    timeZone: timeZone ?? "UTC",
    enabled: ok !== undefined,
  });

  return (
    <TotalsPage
      filters={
        timeZone !== undefined && (
          <>
            <View style={styles.bounds}>
              <TimeBoundFields
                value={{ from, to }}
                onChange={(next) => router.setParams({ from: next.from, to: next.to })}
                fromPlaceholder={`Last ${String(INCIDENT_DEFAULT_DAYS)} days`}
                leading={
                  <TimeZoneField
                    hasSiteFilter={false}
                    value={timeZone}
                    fallback={fallback}
                    at={ok !== undefined ? new Date(ok.filters.since) : new Date()}
                    onChange={(tz) => router.setParams({ tz })}
                  />
                }
              />
            </View>
            <View style={styles.row}>
              <View style={styles.field}>
                <PeriodField value={period} onChange={(next) => router.setParams({ period: next })} />
              </View>
            </View>
          </>
        )
      }
      parsed={parsed}
      totals={totals}
      period={ok?.period ?? "day"}
      incidentsHrefFor={(label) =>
        periodIncidentsHref(
          params.tenantId,
          FLEET_INCIDENTS_SUBPATH,
          { from, to, tz: timeZone ?? "UTC" },
          { period: ok?.period ?? "day", label },
        )
      }
    />
  );
}

/**
 * The filters, then the totals scrolling below them: by day over months the
 * period table runs to many rows.
 */
function TotalsPage({
  filters,
  parsed,
  totals,
  period,
  incidentsHrefFor,
}: {
  readonly filters: ReactNode;
  readonly parsed: ParsedIncidentFilters | undefined;
  readonly totals: Summary;
  readonly period: IncidentSummaryPeriod;
  readonly incidentsHrefFor: (period: string) => Href;
}) {
  return (
    <View style={styles.page}>
      <View style={styles.filters}>{filters}</View>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        {parsed === undefined ?
          <QueryNotice isLoading error={null} onRetry={totals.refetch} />
        : !parsed.ok ?
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        : totals.summary === undefined || totals.error !== null ?
          <QueryNotice isLoading={totals.isLoading} error={totals.error} onRetry={totals.refetch} />
        : <IncidentTotals
            summary={totals.summary}
            period={period}
            isStale={totals.isFetching}
            incidentsHrefFor={incidentsHrefFor}
          />
        }
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filters: {
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the totals, so the pickers open over them.
    zIndex: 1,
  },
  // Above the field below it, so the timezone list and calendars open over it.
  bounds: { zIndex: 1 },
  row: { flexDirection: "row" },
  // The width of the timezone field above it.
  field: { flexBasis: "24%", maxWidth: 420, minWidth: 220 },
  scroll: { flex: 1 },
  content: { paddingHorizontal: theme.spacing.xl, paddingBottom: theme.spacing.xl },
}));
